import { getPartMeasures, UNIT } from 'common/Utils/processNewFileUtils/measureUtils'
import { convertObjToStl } from 'common/Utils/threejsUtils/convertObjToStl'
import { generateImageFromStl } from 'common/Utils/threejsUtils/generateImageFromStl'
import { getStlGeometryData } from 'common/Utils/threejsUtils/getStlGeometryData'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { uploadPartFiles } from 'store/cloudStorage/actions'
import {
	analyzeStepOnShape,
	analyzeStepOpenCascade,
	createPartStepAnalysis,
	deletePartStepAnalysis,
	uploadAndConvertToStep,
} from 'store/parts/actions'

/**
 * @typedef { 'idle'
 * | 'converting_obj'
 * | 'uploading_to_onshape'
 * | 'analyzing_opencascade'
 * | 'analyzing_onshape'
 * | 'generating_image'
 * | 'preview'
 * | 'uploading_files'
 * | 'done'
 * | 'error' } ProcessStatus
 *
 * - `idle`
 * - `converting_obj`
 * - `uploading_to_onshape`    — waiting_redux   `Parts.uploadAndConvertToStepState[partId]`
 * - `analyzing_opencascade`   — waiting_redux   `Parts.analyzeStepOpenCascadeState[partId]`
 * - `analyzing_onshape`       — waiting_redux   `Parts.analyzeStepOnShapeState[partId]`
 * - `generating_image`        — threejs async   (no Redux)
 * - `preview`                 — view and config (no Redux)
 * - `uploading_files`         — waiting_redux   `CloudStorage.uploadPartFilesStateByPartIdState[partId]`
 * - `done` / `error`
 */

/**
 * @typedef { 'cad_error_valid'
 * | 'cad_error_invalid_file'
 * | 'cad_error_multiple_pieces'
 * | 'cad_error_no_piece'
 * | 'cad_error_open_surfaces'
 * | 'cad_error_meshes'
 * | 'cad_error_process'
 * | 'cad_error_malformed'
 * | 'cad_error_not_valid' } CadError
 * 
 * @typedef {{
 *   did: string
 *   wid: string
 *   tid: string
 *   eid: string
 * }} OnShapeIds
 * 
 * @typedef {{
 *   size_mm?: { x?: number; y?: number; z?: number }
 *   volume_cm3?: number
 *   area_cm2?: number
 *   faces?: number
 *   boundaryBoxVolume_cm3?: number
 *   chipVolume_cm3?: number
 * }} CadData
 * 
 * @typedef {{
 *   size_unitless?: { x?: number; y?: number; z?: number },
 *   volume_unitless?: number,
 *   area_unitless?: number,
 * }} StlData
 * 
 * @typedef {{
 *   databaseId:         number | undefined,
 *   stlFile:            File | undefined,
 *   imgFile:            File | undefined,
 *   stlData:            StlData | undefined,
 *   cadData:            CadData | undefined,
 *   stepAnalysis:       any | undefined,
 *   onShapeIds:         OnShapeIds | undefined,
 *   cadError:           CadError | 'unknown_error' | undefined,
 *   finishedProcessing: boolean,
 * }} ProcessResult
 * 
 * @typedef {{ [processId: string]: ProcessStatus }} ProcessStatusMap
 * 
 * @typedef {{ [processId: string]: ProcessResult }} ProcessResultMap
 * 
 * @typedef {{
 *   originalFile:            File,
 *   part:                    { id: number, name: string } | null,
 *   stlFile:                 File | null,
 *   stepFile:                File | null,
 *   objFile:                 File | null,
 *   stlData:                 StlData | null,
 *   cadData:                 CadData | null,
 *   stepAnalysis:            any | null,
 *   stepAnalysisBase64:      string | null,
 *   onShapeIds:              OnShapeIds | null,
 *   imgFile:                 File | null,
 *   filesToUpload?:           { originalFile?: File, stlFile?: File, stepFile?: File, objFile?: File, imgFile: File },
 *   needsStepAnalysis:       boolean,
 *   onShapeTranslatedToStep: boolean,
 *   onShapeAnalyzedStep:     boolean,
 *   openCascadeFailed:       boolean,
 * }} ProcessCtx
 * 
 * @typedef {{ [processId: string]: ProcessCtx }} CtxMap
 * 
 * @typedef {import('../Utils/processNewFileUtils/measureUtils').Unit} Unit
 * 
 * @typedef {import('../Utils/processNewFileUtils/measureUtils').UnitExponent} UnitExponent
 */

/**
 * @param {string} str
 * @returns {str is CadError}
 */
const isCadError = (str) => ([
	'cad_error_valid',
	'cad_error_invalid_file',
	'cad_error_multiple_pieces',
	'cad_error_no_piece',
	'cad_error_open_surfaces',
	'cad_error_meshes',
	'cad_error_process',
	'cad_error_malformed',
	'cad_error_not_valid',
]).includes(str)

/**
 * @example
 *   const { start, processStatuses, result } = useProcessNewFile()
 *
 *   const handleFileSelected = (file) => {
 *     start({ originalFile: file, partId })
 *     // later: processStatuses[partId], result[partId].cadData, etc.
 *   }
 */
const useProcessNewFile = () => {

	const uploadAndConvertToStepState       = useSelector(state => (/** @type {StoreState} */(state)).Parts.uploadAndConvertToStepState)
	const analyzeStepOpenCascadeState       = useSelector(state => (/** @type {StoreState} */(state)).Parts.analyzeStepOpenCascadeState)
	const analyzeStepOnShapeState           = useSelector(state => (/** @type {StoreState} */(state)).Parts.analyzeStepOnShapeState)
	const uploadPartFilesStateByPartIdState = useSelector(state => (/** @type {StoreState} */(state)).CloudStorage.uploadPartFilesStateByPartIdState)
	const createAnalysisStatus              = useSelector(state => (/** @type {StoreState} */(state)).Parts.createAnalysisStatus)
	const dispatch = useDispatch()

	const [processStatuses, setProcessStatuses] = useState(/** @type {ProcessStatusMap} */({}))
	const [result,          setResult]          = useState(/** @type {ProcessResultMap} */({}))
	const ctxMap                                = useRef  (/** @type {CtxMap}           */({}))

	const updateResult = useCallback(
		/**
		 * @param {number} partId
		 * @param {Partial<ProcessResult>} patch
		 */
		(partId, patch) => {
			setResult(previousResult => ({ ...previousResult,
				[partId]: { ...previousResult[partId], ...patch },
			}))
		},
		[],
	)

	const setProcessStatus = useCallback(
		/**
		 * @param {number} partId
		 * @param {ProcessStatus} newStatus
		 */
		(partId, newStatus) => {
			setProcessStatuses(previousStatuses => ({ ...previousStatuses,
				[partId]: newStatus,
			}))
		},
		[],
	)

	const handleError = useCallback(
		/**
		 * @param {number} partId
		 * @param {any} rawError
		 */
		(partId, rawError) => {
			const cadError = isCadError(rawError) ? rawError : 'unknown_error'
			updateResult(partId, { cadError, finishedProcessing: true })
			setProcessStatus(partId, 'error')
		},
		[updateResult, setProcessStatus],
	)

	const toCadData = useCallback(
		/**
		 * @param {StlData} stlData
		 * @returns {CadData}
		 */
		(stlData) => {
			const defaultUnit = UNIT.millimeters
			const partMeasures = getPartMeasures({
				size: { value: stlData.size_unitless, unit: defaultUnit },
				area: { value: stlData.area_unitless, unit: defaultUnit },
				volume: { value: stlData.volume_unitless, unit: defaultUnit },
			})
			return {
				size_mm: partMeasures.size_mm,
				area_cm2: partMeasures.area_cm2,
				volume_cm3: partMeasures.volume_cm3,
				boundaryBoxVolume_cm3: partMeasures.boundaryBoxVolume_cm3,
				chipVolume_cm3: partMeasures.chipVolume_cm3,
			}
		},
		[],
	)

	const runThreeJsAndPrepare = useCallback(
		/**
		 * @param {number} partId
		 * @param {File} stlFile
		 */
		async (partId, stlFile) => {
			const currentCtx = ctxMap.current[partId]
			try {
				// Only compute geometry from STL if we don't already have cadData from OpenCascade/OnShape.
				// That happens when the original file is STL or OBJ (no STEP analysis available).
				if (!currentCtx.cadData) {
					const { stlData } = await getStlGeometryData(stlFile)
					const cadData = toCadData(stlData)
					currentCtx.stlData = stlData
					currentCtx.cadData = cadData
					updateResult(partId, { stlFile, stlData, cadData })
				} else {
					updateResult(partId, { stlFile })
				}

				setProcessStatus(partId, 'generating_image')
				const { imgFile } = await generateImageFromStl(stlFile)
				currentCtx.imgFile = imgFile
				updateResult(partId, { imgFile })

				// Build the upload payload now and store it — accept() will dispatch it later
				currentCtx.filesToUpload = {
					originalFile: currentCtx.originalFile,
					objFile:      currentCtx.objFile,
					stepFile:     currentCtx.stepFile,
					stlFile:      currentCtx.stlFile,
					imgFile:      currentCtx.imgFile,
				}
				if (currentCtx.onShapeTranslatedToStep) {
					delete currentCtx.filesToUpload.originalFile  // onShape already uploads the original file on upload
					delete currentCtx.filesToUpload.stepFile      // onShape already uploads the STEP file on upload
				}
				if (currentCtx.onShapeAnalyzedStep) {
					delete currentCtx.filesToUpload.stlFile       // onShape already uploads the STL file on analysis
				}

				// Ready for user to preview and confirm
				setProcessStatus(partId, 'preview')
			} catch (error) {
				handleError(partId, error)
			}
		},
		[toCadData, updateResult, setProcessStatus, handleError],
	)

	const routeAfterPartReady = useCallback(
		/**
		 * @param {number} partId
		 * @param {{ id: number, name: string }} part
		 */
		(partId, part) => {
			const currentCtx = ctxMap.current[partId]
			const extension = currentCtx.originalFile.name.split('.').pop()?.toLowerCase() ?? ''

			if (extension === 'obj') {
				setProcessStatus(partId, 'converting_obj');
				(async () => {
					try {
						const { stlFile } = await convertObjToStl(currentCtx.originalFile)
						currentCtx.objFile = currentCtx.originalFile
						currentCtx.stlFile = stlFile
						await runThreeJsAndPrepare(partId, stlFile)
					} catch (error) { handleError(partId, error) }
				})()

			} else if (extension === 'stl') {
				currentCtx.stlFile = currentCtx.originalFile
				runThreeJsAndPrepare(partId, currentCtx.originalFile)

			} else if (extension === 'stp' || extension === 'step') {
				currentCtx.stepFile = currentCtx.originalFile
				dispatch(analyzeStepOpenCascade(
					{ partId: part.id, stepFile: currentCtx.originalFile },
					{ id: part.id },
				))
				setProcessStatus(partId, 'analyzing_opencascade')

			} else {
				// Unknown format → upload to OnShape first to convert to STEP
				currentCtx.onShapeTranslatedToStep = true
				dispatch(uploadAndConvertToStep(
					{ partId: part.id, otherFile: currentCtx.originalFile },
					{ id: part.id },
				))
				setProcessStatus(partId, 'uploading_to_onshape')
			}
		},
		[dispatch, handleError, setProcessStatus, runThreeJsAndPrepare],
	)

	/**
	 * Starts processing a new file.
	 */
	const start = useCallback(
		/**
		 * @param {{ originalFile: File, partId: number }} args
		*/
		({ originalFile, partId }) => {
			const part = { id: partId, name: originalFile.name }

			ctxMap.current[partId] = {
				originalFile,
				part,
				stlFile: null,
				stepFile: null,
				objFile: null,
				stlData: null,
				cadData: null,
				stepAnalysis: null,
				stepAnalysisBase64: null,
				onShapeIds: null,
				imgFile: null,
				filesToUpload: null,
				needsStepAnalysis: false,
				onShapeTranslatedToStep: false,
				onShapeAnalyzedStep: false,
				openCascadeFailed: false,
			}

			setResult(previousResult => ({ ...previousResult,
				[partId]: {
					databaseId: partId, stlFile: undefined, imgFile: undefined,
					stlData: undefined, cadData: undefined, stepAnalysis: undefined,
					cadError: undefined, finishedProcessing: false,
				},
			}))

			routeAfterPartReady(partId, part)
		},
		[routeAfterPartReady],
	)

	/**
	 * Clears a cancelled, finished, or errored process from ongoing processes map and results map
	 */
	const reset = useCallback(
		/**
		 * @param {number} partId
		 */
		(partId) => {
			delete ctxMap.current[partId]
			setProcessStatuses(previousStatuses => {
				const next = { ...previousStatuses }
				delete next[partId]
				return next
			})
			setResult(previousResult => {
				const next = { ...previousResult }
				delete next[partId]
				return next
			})
		},
		[],
	)

	/**
	 * Called by the consumer after the user reviews the preview and accepts.
	 * Triggers createPartStepAnalysis (if applicable) and uploadPartFiles.
	 * After dispatching, status moves to 'uploading_files' and the existing
	 * uploading_files useEffect handles the rest (→ finishedProcessing → 'done').
	 */
	const accept = useCallback(
		/**
		 * @param {number} partId
		 */
		(partId) => {
			const currentCtx = ctxMap.current[partId]
			if (!currentCtx) return

			const extension = currentCtx.originalFile.name.split('.').pop()?.toLowerCase() ?? ''

			// Persist step analysis to DB (tracked — we wait for it in the uploading_files effect)
			if (currentCtx.stepAnalysis) {
				currentCtx.needsStepAnalysis = true
				dispatch(createPartStepAnalysis(currentCtx.part.id, currentCtx.stepAnalysisBase64))
			} else if (extension === 'stl' || extension === 'obj') {
				// OBJ/STL files can't have a step analysis — delete any existing one
				dispatch(deletePartStepAnalysis(currentCtx.part.id))
			}

			// Upload all files
			const filesToUpload = currentCtx.filesToUpload
			dispatch(uploadPartFiles({
				body: {
					data: {
						partId:     currentCtx.part.id,
						filename:   currentCtx.originalFile.name,
					},
					files: {
						thumbnail:  filesToUpload?.imgFile,
						original3D: filesToUpload?.originalFile,
						modified3D: filesToUpload?.stepFile,
						modelStl:   filesToUpload?.stlFile,
						modelObj:   filesToUpload?.objFile,
					},
				},
			}, { id: currentCtx.part.id }))

			setProcessStatus(partId, 'uploading_files')
		},
		[dispatch, setProcessStatus],
	)

	/**
	 * Re-interprets the raw STL geometry data as a different unit and updates cadData.
	 * Only has an effect for STL files (those that went through the Three.js path and have stlData).
	 */
	const setUnit = useCallback(
		/**
		 * @param {number} partId
		 * @param {Unit} unit
		 */
		(partId, unit) => {
			const currentCtx = ctxMap.current[partId]
			if (!currentCtx?.stlData) return
			currentCtx.cadData = getPartMeasures({
				size: { value: currentCtx.stlData.size_unitless, unit },
				area: { value: currentCtx.stlData.area_unitless, unit },
				volume: { value: currentCtx.stlData.volume_unitless, unit },
			})
			updateResult(partId, { cadData: currentCtx.cadData })
		},
		[updateResult],
	)

	// ─── useEffects of the process ────────────────────────────────────────────────────
	// Each effect finds all partIds currently at the relevant status and handles them.
	// This is what allows concurrent processing within a single hook instance.

	/**
	 * Returns all partIds currently at a given status.
	 * @param {ProcessStatus} targetStatus
	 * @returns {number[]}
	 */
	const getPartIdsByStatus = (targetStatus) =>
		Object.entries(processStatuses)
			.filter(([, currentStatus]) => currentStatus === targetStatus)
			.map(([partId]) => Number(partId))

	// ─── Status: uploading_to_onshape ─────────────────────────────────────────
	useEffect(() => {
		for (const partId of getPartIdsByStatus('uploading_to_onshape')) {

			const currentCtx = ctxMap.current[partId]
			if (!currentCtx.part) continue

			const sliceEntry = uploadAndConvertToStepState[currentCtx.part.id]
			if (!sliceEntry || sliceEntry.status !== 'finished') continue

			if (sliceEntry.error) {
				handleError(partId, sliceEntry.error)
				continue
			}

			const { stepFile, onShapeIds } = sliceEntry.response
			currentCtx.stepFile = stepFile
			currentCtx.onShapeIds = onShapeIds

			// Store OnShape IDs in result so Update3DModelModal includes them in the
			// main updateParts call after Accept (which already has error handling).
			updateResult(partId, { onShapeIds })

			if (currentCtx.openCascadeFailed) {
				// Already tried OpenCascade for this STEP (OTHER-file path), go straight to OnShape analyze
				currentCtx.onShapeAnalyzedStep = true
				dispatch(analyzeStepOnShape(
					{ onShapeIds, partId: currentCtx.part.id, filename: currentCtx.originalFile.name },
					{ id: currentCtx.part.id },
				))
				setProcessStatus(partId, 'analyzing_onshape')
			} else {
				// First visit (OTHER-file path): try OpenCascade on the STEP we just received
				dispatch(analyzeStepOpenCascade(
					{ partId: currentCtx.part.id, stepFile },
					{ id: currentCtx.part.id },
				))
				setProcessStatus(partId, 'analyzing_opencascade')
			}
		}
	}, [processStatuses, uploadAndConvertToStepState, dispatch, handleError, setProcessStatus])

	// ─── Status: analyzing_opencascade ────────────────────────────────────────
	useEffect(() => {
		for (const partId of getPartIdsByStatus('analyzing_opencascade')) {
			const currentCtx = ctxMap.current[partId]
			if (!currentCtx.part) continue
			const sliceEntry = analyzeStepOpenCascadeState[currentCtx.part.id]
			if (!sliceEntry || sliceEntry.status !== 'finished') continue
			if (currentCtx.cadData) continue

			if (sliceEntry.error) {
				if (isCadError(sliceEntry.error)) { handleError(partId, sliceEntry.error); continue }

				// Non-fatal OpenCascade error → fall back to OnShape
				currentCtx.openCascadeFailed = true

				if (currentCtx.onShapeIds) {
					// OTHER-file path: OnShape IDs already available from the earlier upload status
					currentCtx.onShapeAnalyzedStep = true
					dispatch(analyzeStepOnShape(
						{ onShapeIds: currentCtx.onShapeIds, partId: currentCtx.part.id, filename: currentCtx.originalFile.name },
						{ id: currentCtx.part.id },
					))
					setProcessStatus(partId, 'analyzing_onshape')
				} else {
					// STEP-file path: need to upload to OnShape first to get OnShape IDs
					currentCtx.onShapeTranslatedToStep = true
					dispatch(uploadAndConvertToStep(
						{ partId: currentCtx.part.id, otherFile: currentCtx.stepFile ?? currentCtx.originalFile },
						{ id: currentCtx.part.id },
					))
					setProcessStatus(partId, 'uploading_to_onshape')
				}
				continue
			}

			const { stepAnalysis, stepAnalysisBase64, stlFile } = sliceEntry.response

			if (stepAnalysis.validity?.isValid === false) {
				handleError(partId, 'cad_error_not_valid')
				continue
			}

			const cadData = {
				size_mm: {
					x: stepAnalysis.boundingBox.xSize_mm,
					y: stepAnalysis.boundingBox.ySize_mm,
					z: stepAnalysis.boundingBox.zSize_mm,
				},
				volume_cm3: stepAnalysis.properties.volume_cm3,
				area_cm2: stepAnalysis.properties.surfaceArea_cm2,
				faces: stepAnalysis.topology.faces,
				boundaryBoxVolume_cm3: stepAnalysis.properties.boundingBoxVolume_cm3,
				chipVolume_cm3: stepAnalysis.properties.chipVolume_cm3,
			}
			currentCtx.cadData = cadData
			currentCtx.stlFile = stlFile
			currentCtx.stepAnalysis = stepAnalysis
			currentCtx.stepAnalysisBase64 = stepAnalysisBase64
			updateResult(partId, { cadData, stlFile, stepAnalysis })
			runThreeJsAndPrepare(partId, stlFile)
		}
	}, [processStatuses, analyzeStepOpenCascadeState, dispatch, handleError, updateResult, setProcessStatus, runThreeJsAndPrepare])

	// ─── Status: analyzing_onshape ────────────────────────────────────────────
	useEffect(() => {
		for (const partId of getPartIdsByStatus('analyzing_onshape')) {
			const currentCtx = ctxMap.current[partId]
			if (!currentCtx.part) continue
			const sliceEntry = analyzeStepOnShapeState[currentCtx.part.id]
			if (!sliceEntry || sliceEntry.status !== 'finished') continue

			if (sliceEntry.error) { handleError(partId, sliceEntry.error); continue }

			const { cadData, stlFile } = sliceEntry.response
			currentCtx.cadData = cadData
			currentCtx.stlFile = stlFile
			updateResult(partId, { cadData, stlFile })

			runThreeJsAndPrepare(partId, stlFile)
		}
	}, [processStatuses, analyzeStepOnShapeState, handleError, updateResult, runThreeJsAndPrepare])

	// ─── Status: uploading_files ──────────────────────────────────────────────
	useEffect(() => {
		for (const partId of getPartIdsByStatus('uploading_files')) {
			const currentCtx = ctxMap.current[partId]
			if (!currentCtx.part) continue
			const sliceEntry = uploadPartFilesStateByPartIdState[currentCtx.part.id]
			if (!sliceEntry || sliceEntry.status !== 'finished') continue

			if (sliceEntry.error) { handleError(partId, sliceEntry.error); continue }

			// If createPartStepAnalysis was dispatched, wait for it to finish too
			if (currentCtx.needsStepAnalysis) {
				const analysisEntry = createAnalysisStatus[currentCtx.part.id]
				if (analysisEntry === 'creating...') continue
				if (analysisEntry instanceof Error) { handleError(partId, analysisEntry); continue }
			}

			updateResult(partId, { finishedProcessing: true })
			setProcessStatus(partId, 'done')
		}
	}, [processStatuses, uploadPartFilesStateByPartIdState, createAnalysisStatus, handleError, updateResult, setProcessStatus])

	return { start, reset, accept, setUnit, processStatuses, result }
}

export default useProcessNewFile
